import { FC, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import { ImageUpload } from '@/components';
import AdminLessonEditor from './AdminLessonEditor';
import apiService from '@/services/api.service';
import type { AdminTag, AdminTrail, AdminLesson } from '@/types/admin';

const CATEGORIES = ['IA Generativa', 'Automação', 'Liderança', 'Inovação', 'Dados', 'Produtividade'];
const LEVELS: Array<{ value: string; label: string }> = [
  { value: 'BEGINNER', label: 'Iniciante' },
  { value: 'INTERMEDIATE', label: 'Intermediário' },
  { value: 'ADVANCED', label: 'Avançado' },
];

const emptyTrail = {
  title: '',
  description: '',
  category: 'IA Generativa',
  level: 'BEGINNER',
  thumbnail: '',
  duration_minutes: 60,
  xp_reward: 1200,
  sector_id: '' as string,
};

const AdminCourseFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [form, setForm] = useState(emptyTrail);
  const [allTags, setAllTags] = useState<AdminTag[]>([]);
  const [sectors, setSectors] = useState<{ id: string; name: string }[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [lessons, setLessons] = useState<AdminLesson[]>([]);
  const [editingLesson, setEditingLesson] = useState<AdminLesson | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState<'data' | 'lessons'>('data');
  const [newTagName, setNewTagName] = useState('');

  useEffect(() => {
    loadTags();
    loadSectors();
    if (isEditing) loadTrail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = async () => {
    try {
      const tags = await adminService.getTags();
      setAllTags(tags);
    } catch { /* ignore */ }
  };

  const loadSectors = async () => {
    try {
      const { data } = await apiService.get<{ id: string; name: string }[]>('/sectors/');
      setSectors(data);
    } catch { /* ignore */ }
  };

  const loadTrail = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const trail: AdminTrail = await adminService.getTrail(id);
      setForm({
        title: trail.title,
        description: trail.description,
        category: trail.category,
        level: trail.level,
        thumbnail: trail.thumbnail,
        duration_minutes: trail.duration_minutes,
        xp_reward: trail.xp_reward,
        sector_id: trail.sector?.id || '',
      });
      setSelectedTagIds(trail.tags.map((t) => t.id));
      setLessons(trail.lessons || []);
    } catch {
      showToast('Erro ao carregar curso', 'error');
      navigate('/admin/cursos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast('Título é obrigatório', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        tag_ids: selectedTagIds,
        sector_id: form.sector_id || null,
      };
      if (isEditing && id) {
        await adminService.updateTrail(id, payload);
      } else {
        const created = await adminService.createTrail(payload);
        navigate(`/admin/cursos/${created.id}/editar`, { replace: true });
        return;
      }
      await loadTrail();
    } catch {
      showToast('Erro ao salvar curso', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const tag = await adminService.createTag({ name: newTagName.trim() });
      setAllTags((prev) => [...prev, tag]);
      setSelectedTagIds((prev) => [...prev, tag.id]);
      setNewTagName('');
    } catch {
      showToast('Erro ao criar tag', 'error');
    }
  };

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleAddLesson = async () => {
    if (!id) {
      showToast('Salve o curso primeiro para adicionar páginas', 'warning');
      return;
    }
    try {
      const lesson = await adminService.createLesson(id, {
        title: `Nova Página ${lessons.length + 1}`,
        content: { markdown: '', youtube_url: null, attachments: [] },
        duration_minutes: 10,
        min_time_seconds: 60,
        order: lessons.length,
      });
      setLessons((prev) => [...prev, lesson]);
      setEditingLesson(lesson);
    } catch {
      showToast('Erro ao criar página', 'error');
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!id || !confirm('Excluir esta página?')) return;
    try {
      await adminService.deleteLesson(id, lessonId);
      setLessons((prev) => prev.filter((l) => l.id !== lessonId));
    } catch {
      showToast('Erro ao excluir página', 'error');
    }
  };

  const handleMoveLesson = async (index: number, direction: 'up' | 'down') => {
    if (!id) return;
    const newLessons = [...lessons];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newLessons.length) return;
    [newLessons[index], newLessons[swapIdx]] = [newLessons[swapIdx], newLessons[index]];
    setLessons(newLessons);
    try {
      await adminService.reorderLessons(id, newLessons.map((l) => l.id));
    } catch {
      await loadTrail(); // revert
    }
  };

  const handleLessonSaved = useCallback((updatedLesson: AdminLesson) => {
    setLessons((prev) => prev.map((l) => (l.id === updatedLesson.id ? updatedLesson : l)));
    setEditingLesson(null);
  }, []);

  /** O upload direto grava `Lesson.content` no servidor no momento do commit,
   *  sem passar pelo Salvar. Atualiza a lista E a lição em edição, senão a
   *  cópia em memória continua sem o vídeo e um Cancelar o faria "sumir". */
  const handleLessonMutated = useCallback((mutated: AdminLesson) => {
    setLessons((prev) => prev.map((l) => (l.id === mutated.id ? mutated : l)));
    setEditingLesson((prev) => (prev && prev.id === mutated.id ? mutated : prev));
  }, []);

  if (isLoading) {
    return <div className="p-10 text-content-muted">Carregando...</div>;
  }

  if (editingLesson && id) {
    return (
      <AdminLessonEditor
        trailId={id}
        lesson={editingLesson}
        onSave={handleLessonSaved}
        onLessonMutated={handleLessonMutated}
        onCancel={() => setEditingLesson(null)}
      />
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin/cursos')} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Editar Curso' : 'Novo Curso'}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-theme-border">
        <button
          onClick={() => setTab('data')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors -mb-px ${
            tab === 'data' ? 'border-accent text-accent' : 'border-transparent text-content-muted hover:text-content-primary'
          }`}
        >
          Dados do Curso
        </button>
        <button
          onClick={() => setTab('lessons')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors -mb-px ${
            tab === 'lessons' ? 'border-accent text-accent' : 'border-transparent text-content-muted hover:text-content-primary'
          }`}
        >
          Páginas ({lessons.length})
        </button>
      </div>

      {tab === 'data' ? (
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              placeholder="Nome do curso"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none resize-none"
              placeholder="Descrição do curso"
            />
          </div>

          {/* Category + Level */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Categoria</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nível</label>
              <select
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* XP + Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">XP Total</label>
              <input
                type="number"
                value={form.xp_reward}
                onChange={(e) => setForm({ ...form, xp_reward: Number(e.target.value) })}
                onFocus={(e) => e.target.select()}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                min={0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duração (min)</label>
              <input
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                onFocus={(e) => e.target.select()}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                min={0}
              />
            </div>
          </div>

          {/* Thumbnail */}
          <ImageUpload
            value={form.thumbnail}
            onChange={(url) => setForm({ ...form, thumbnail: url })}
            folder="thumbnails"
            label="Imagem do Curso"
            hint="Recomendado: 800x400px, JPEG ou PNG"
          />

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {allTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleToggleTag(tag.id)}
                  className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                    selectedTagIds.includes(tag.id)
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-theme-border text-content-muted hover:text-content-primary'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Nova tag..."
                className="px-3 py-1.5 rounded-lg bg-surface-card border border-theme-border focus:border-accent focus:outline-none text-sm"
              />
              <button
                onClick={handleAddTag}
                className="px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-sm font-medium hover:bg-accent/20"
              >
                Criar
              </button>
            </div>
          </div>

          {/* Sector */}
          <div>
            <label className="block text-sm font-medium mb-1">Setor</label>
            <select
              value={form.sector_id}
              onChange={(e) => setForm({ ...form, sector_id: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              <option value="">Sem setor (visível a todos)</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <p className="text-xs text-content-muted mt-1">
              Cursos com setor são visíveis apenas para usuários do setor selecionado.
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Curso'}
            </button>
          </div>
        </div>
      ) : (
        /* Lessons Tab */
        <div className="space-y-4">
          {!isEditing && (
            <p className="text-content-muted text-sm">Salve o curso primeiro para poder adicionar páginas.</p>
          )}

          {lessons.map((lesson, idx) => (
            <div
              key={lesson.id}
              className="bg-surface-card border border-theme-border rounded-xl p-4 flex items-center gap-4"
            >
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleMoveLesson(idx, 'up')}
                  disabled={idx === 0}
                  className="text-content-muted hover:text-content-primary disabled:opacity-20"
                >
                  <span className="material-symbols-outlined text-lg">keyboard_arrow_up</span>
                </button>
                <button
                  onClick={() => handleMoveLesson(idx, 'down')}
                  disabled={idx === lessons.length - 1}
                  className="text-content-muted hover:text-content-primary disabled:opacity-20"
                >
                  <span className="material-symbols-outlined text-lg">keyboard_arrow_down</span>
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{lesson.title}</p>
                <p className="text-content-muted text-sm">{lesson.xp_reward} XP</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditingLesson(lesson)}
                  className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary"
                  title="Editar"
                >
                  <span className="material-symbols-outlined text-xl">edit</span>
                </button>
                <button
                  onClick={() => handleDeleteLesson(lesson.id)}
                  className="p-2 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400"
                  title="Excluir"
                >
                  <span className="material-symbols-outlined text-xl">delete</span>
                </button>
              </div>
            </div>
          ))}

          {isEditing && (
            <button
              onClick={handleAddLesson}
              className="w-full border-2 border-dashed border-theme-border rounded-xl p-4 text-content-muted hover:text-accent hover:border-accent/40 transition-colors flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined">add</span>
              Adicionar Página
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCourseFormPage;
